import { apiClient, setLastSignInTime } from '@/lib/api/client'
import type { User } from '@/types/entities'
import type { LoginCredentials, RegisterData } from '../types'

export interface AvailabilityResponse {
  available: boolean
  message: string
}

/**
 * Base64 for the `encp` field, safe for any password.
 *
 * btoa() throws InvalidCharacterError on anything outside Latin-1, so a password with an
 * accented letter, a curly quote pasted from elsewhere, or any non-Latin script made the
 * request throw before it was sent — no network call, and an error that says nothing
 * about passwords. UTF-8 first, then base64; the server does
 * Buffer.from(value, 'base64').toString(), which decodes UTF-8, so this round-trips.
 */
function encodePassword (password: string): string {
  const utf8 = new TextEncoder().encode(password)
  let binary = ''
  for (const byte of utf8) binary += String.fromCharCode(byte)
  return btoa(binary)
}

export const authApi = {
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/api/auth')
    return response.data
  },

  /**
   * Resolve a user with an EXPLICIT token rather than whatever is in the store.
   *
   * Needed when a token exists but is not yet the session's — beginning a masquerade, or a
   * Supabase sign-in — where asking "who is this token" has to happen before deciding to adopt it.
   */
  getCurrentUserWithToken: async (token: string): Promise<User> => {
    const response = await apiClient.get<User>('/api/auth', {
      headers: { Authorization: `Bearer ${token}` },
    })
    return response.data
  },

  signIn: async (credentials: LoginCredentials): Promise<User> => {
    // The server reads `encp` and treats an empty string as "no password supplied",
    // answering 400 — which reaches the user as a vague "something was not valid"
    // rather than anything they can act on. Fail here instead, where the message can
    // say what is actually wrong.
    if (!credentials.email || !credentials.password) {
      throw new Error('Enter your email and password.')
    }

    // Send credentials in request body for Firebase Hosting compatibility
    // Firebase Hosting may strip custom headers when proxying to Cloud Run
    const response = await apiClient.post<User>('/api/auth/signin', {
      email: credentials.email,
      encp: encodePassword(credentials.password),
      turnstileToken: credentials.turnstileToken,
    })
    
    // Mark sign-in time to prevent immediate 401 redirects (allows cookie to propagate)
    setLastSignInTime()
    
    return response.data
  },

  signOut: async (): Promise<void> => {
    await apiClient.delete('/api/auth')
  },

  register: async (data: RegisterData): Promise<User> => {
    const response = await apiClient.post<User>(
      '/api/users',
      {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        artisticName: data.artisticName,
        // The sign-up form asks for this; artists.create writes it. Omitting it here was why the
        // bio an artist typed at sign-up never reached the account it was typed into.
        bio: data.bio,
        turnstileToken: data.turnstileToken,
      },
      {
        headers: {
          encp: encodePassword(data.password),
        },
      }
    )
    return response.data
  },

  sendResetInstructions: async (email: string, turnstileToken?: string): Promise<void> => {
    await apiClient.get('/api/auth/reset-instructions', {
      headers: { 
        email,
        ...(turnstileToken && { 'x-turnstile-token': turnstileToken }),
      },
    })
  },

  resetPassword: async (email: string, token: string, password: string): Promise<void> => {
    await apiClient.get('/api/auth/reset-password', {
      headers: {
        email,
        token,
        encp: encodePassword(password),
      },
    })
  },

  verifyEmail: async (email: string, token: string): Promise<void> => {
    await apiClient.get('/api/auth/verify', {
      headers: { email, token },
    })
  },

  /**
   * Check if an artist name is available for registration
   */
  checkArtistNameAvailable: async (name: string): Promise<AvailabilityResponse> => {
    const response = await apiClient.get<AvailabilityResponse>('/api/users/check-artist-name', {
      params: { name },
    })
    return response.data
  },

  /**
   * Check if an email is available for registration
   */
  checkEmailAvailable: async (email: string): Promise<AvailabilityResponse> => {
    const response = await apiClient.get<AvailabilityResponse>('/api/users/check-email', {
      params: { email },
    })
    return response.data
  },
}

